// Format hours as Hh Mm Ss (copied from TimeSheet)
const formatHoursHms = (hours) => {
  const value = parseFloat(hours);
  if (!Number.isFinite(value) || value <= 0) return '0h 0m 0s';
  let totalSeconds = Math.round(value * 3600);
  const h = Math.floor(totalSeconds / 3600);
  totalSeconds %= 3600;
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${h}h ${m}m ${s}s`;
};

// Helper: format a value as IST date (dd-MM-YYYY)
const formatDateIST = (value) => {
  if (!value) return '';
  const base = new Date(value);
  if (Number.isNaN(base.getTime())) return String(value);
  const ist = new Date(base.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const dd = String(ist.getDate()).padStart(2, '0');
  const mm = String(ist.getMonth() + 1).padStart(2, '0');
  const yyyy = ist.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

// Helper: format a value as IST datetime (YYYY-MM-DD HH:MM:SS)
const formatDateTimeIST = (value) => {
  if (!value) return '';
  const base = new Date(value);
  if (Number.isNaN(base.getTime())) return String(value);
  const ist = new Date(base.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const yyyy = ist.getFullYear();
  const mm = String(ist.getMonth() + 1).padStart(2, '0');
  const dd = String(ist.getDate()).padStart(2, '0');
  const hh = String(ist.getHours()).padStart(2, '0');
  const min = String(ist.getMinutes()).padStart(2, '0');
  const ss = String(ist.getSeconds()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
};

import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart, 
  Pie, 
  Cell 
} from 'recharts';
import { Download, Printer, Calendar, User } from 'lucide-react';
import { exportToExcel, formatDataForExport } from '../utils/excelExport';

// Move these helpers above the component so they are available for useState initializers


const toDateInput = (dateObj) => {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return '';
  return dateObj.toISOString().split('T')[0];
};

const fromDateInput = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const resolveUserIdentifier = (user) => {
  if (!user) return '';
  if (typeof user === 'string') return user;
  if (user.id) return String(user.id);
  if (user._id) {
    if (typeof user._id === 'string') return user._id;
    if (user._id.$oid) return user._id.$oid;
    return String(user._id);
  }
  if (user.user_id) return String(user.user_id);
  return '';
};

const Reports = () => {
  const { token, user } = useAuth();
  const [timeRange, setTimeRange] = useState('week');
  const [reportData, setReportData] = useState(null);
  const [teamData, setTeamData] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 7)));
  const [endDate, setEndDate] = useState(new Date());
  const [exportType, setExportType] = useState('time-tracking');
  // New: User time tracking summary state
  const [userSummary, setUserSummary] = useState([]);
  const [userSummaryLoading, setUserSummaryLoading] = useState(false);
  const [userSummaryPeriod, setUserSummaryPeriod] = useState('today');
  const [userSummaryFrom, setUserSummaryFrom] = useState(() => toDateInput(new Date()));
  const [userSummaryTo, setUserSummaryTo] = useState(() => toDateInput(new Date()));
  const [userSummaryUser, setUserSummaryUser] = useState([]);
  const [userSummaryTriggered, setUserSummaryTriggered] = useState(false);
  // Modal state for user summary table
  const [showUserSummaryModal, setShowUserSummaryModal] = useState(false);

  // User filter for Time Tracking Summary (single-select like TimeSheet)
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [userDropdownSearch, setUserDropdownSearch] = useState('');

  // Teams state
  const [teams, setTeams] = useState([]);

  // Team Utilization state
  const [teamUtilFrom, setTeamUtilFrom] = useState(() => toDateInput(new Date()));
  const [teamUtilTo, setTeamUtilTo] = useState(() => toDateInput(new Date()));
  const [teamUtilTeams, setTeamUtilTeams] = useState([]);
  const [teamUtilLoading, setTeamUtilLoading] = useState(false);
  const [showTeamUtilModal, setShowTeamUtilModal] = useState(false);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [teamDropdownSearch, setTeamDropdownSearch] = useState('');

  // Completed Projects Report state
  const [clients, setClients] = useState([]);
  const [completedProjFrom, setCompletedProjFrom] = useState(() => toDateInput(new Date()));
  const [completedProjTo, setCompletedProjTo] = useState(() => toDateInput(new Date()));
  const [completedProjClients, setCompletedProjClients] = useState([]);
  const [completedProjData, setCompletedProjData] = useState([]);
  const [completedProjLoading, setCompletedProjLoading] = useState(false);
  const [showCompletedProjModal, setShowCompletedProjModal] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [clientDropdownSearch, setClientDropdownSearch] = useState('');

  // Active Projects Report state
  const [activeProjFrom, setActiveProjFrom] = useState('');
  const [activeProjTo, setActiveProjTo] = useState('');
  const [activeProjClients, setActiveProjClients] = useState([]);
  const [activeProjData, setActiveProjData] = useState([]);
  const [activeProjLoading, setActiveProjLoading] = useState(false);
  const [showActiveProjModal, setShowActiveProjModal] = useState(false);
  const [showActiveClientDropdown, setShowActiveClientDropdown] = useState(false);
  const [activeClientDropdownSearch, setActiveClientDropdownSearch] = useState('');

  // User Task Reports state (summary & details)
  const [taskSummaryFrom, setTaskSummaryFrom] = useState(() => toDateInput(new Date()));
  const [taskSummaryTo, setTaskSummaryTo] = useState(() => toDateInput(new Date()));
  const [taskSummaryLoading, setTaskSummaryLoading] = useState(false);
  const [taskDetailsFrom, setTaskDetailsFrom] = useState(() => toDateInput(new Date()));
  const [taskDetailsTo, setTaskDetailsTo] = useState(() => toDateInput(new Date()));

  // Today Job Info state
  const [todayJobInfoDate, setTodayJobInfoDate] = useState(() => toDateInput(new Date()));
  const [todayJobInfoLoading, setTodayJobInfoLoading] = useState(false);

  // Error Report (External / Internal) state
  const [errorReportFrom, setErrorReportFrom] = useState(() => toDateInput(new Date()));
  const [errorReportTo, setErrorReportTo] = useState(() => toDateInput(new Date()));
  const [errorReportClients, setErrorReportClients] = useState([]);
  const [errorReportType, setErrorReportType] = useState('external');
  const [errorReportMode, setErrorReportMode] = useState('date'); // 'date' | 'job'
  const [errorReportJobCodes, setErrorReportJobCodes] = useState('');
  const [errorReportLoading, setErrorReportLoading] = useState(false);
  const [showErrorClientDropdown, setShowErrorClientDropdown] = useState(false);
  const [errorClientDropdownSearch, setErrorClientDropdownSearch] = useState('');

  // Fetch user summary only when 'View' is clicked
  const fetchUserSummary = async () => {
    if (!token) return;
    setUserSummaryLoading(true);
    try {
      // Add 1 day to endDate to include the entire end date
      const fromDate = new Date(userSummaryFrom);
      const toDate = new Date(userSummaryTo);
      toDate.setDate(toDate.getDate() + 1);
      
      const params = {
        startDate: userSummaryFrom,
        endDate: toDate.toISOString().split('T')[0],
        period: userSummaryPeriod
      };
      
      // Add userId filter if users are selected - send as comma-separated string
      if (userSummaryUser && userSummaryUser.length > 0) {
        params.userId = userSummaryUser.join(',');
      }
      // console.log('fetchUserSummary user ids', userSummaryUser);
      // console.log('fetchUserSummary params->', params);
      
      const resp = await api.get('/reports/user-time-tracking', {
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });
      setUserSummary(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      console.error('Failed to fetch user summary', err);
      setUserSummary([]);
    } finally {
      setUserSummaryLoading(false);
    }
  };
  // Helper: convert decimal hours (e.g. 10.95) to H.MM format (10.57)
  // where the fraction represents minutes (0.95 hours -> 57 minutes).
  const formatHoursToHM = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (!isFinite(num)) return value;

    const isNegative = num < 0;
    let totalMinutes = Math.round(Math.abs(num) * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const formatted = `${hours}.${String(minutes).padStart(2, '0')}`;
    return isNegative ? `-${formatted}` : formatted;
  };

  // Export user summary as CSV
  const handleExportUserSummary = () => {
    if (!userSummary || userSummary.length === 0) {
      alert('No user summary data to export');
      return;
    }

    const baseData = formatDataForExport(userSummary || [], {
      user_name: 'User',
      department: 'Department',
      role: 'Role',
      team: 'Team',
      total_shift_hours: 'Total Shift Hours',
      job_hours: 'Job Hours',
      break_hours: 'Break Hours',
      break_lunch: 'Break Lunch',
      break_snacks: 'Break Snacks',
      break_dinner: 'Break Dinner',
      break_restroom: 'Break Restroom',
      idle_hours: 'Idle Hours',
      idle_meeting: 'Idle Meeting',
      idle_call: 'Idle Call',
      idle_training: 'Idle Training',
      idle_no_jobs: 'No Jobs Hrs',
      non_tracking_hours: 'Non Tracking Hrs',
      tasks_done: 'Tasks Done',
      projects_done: 'Projects Done'
    });

    const hourColumns = [
      'Total Shift Hours',
      'Job Hours',
      'Break Hours',
      'Break Lunch',
      'Break Snacks',
      'Break Dinner',
      'Break Restroom',
      'Idle Hours',
      'Idle Meeting',
      'Idle Call',
      'Idle Training',
      'No Jobs Hrs',
      'Non Tracking Hrs'
    ];

    const formattedData = baseData.map((row) => {
      const next = { ...row };
      hourColumns.forEach((col) => {
        if (Object.prototype.hasOwnProperty.call(next, col)) {
          next[col] = formatHoursToHM(next[col]);
        }
      });
      return next;
    });

    exportToExcel(formattedData, `user_time_summary_${userSummaryFrom}_to_${userSummaryTo}.csv`);
  };

  const isAdmin = user && user.role === 'admin';
  const isPM = user && user.role === 'pm';
  const isLead = user && typeof user.role === 'string' && user.role.endsWith('_lead');
  const activeUsers = users.filter(u => u.on_shift);

  // Mock data
  const mockData = {
    jobsByStatus: [
      { name: 'Completed', value: 45 },
      { name: 'In Progress', value: 25 },
      { name: 'Pending', value: 15 },
      { name: 'On Hold', value: 5 },
    ],
    productivity: [
      { name: 'Mon', hours: 6.5 },
      { name: 'Tue', hours: 7.2 },
      { name: 'Wed', hours: 5.8 },
      { name: 'Thu', hours: 8.1 },
      { name: 'Fri', hours: 6.9 },
    ],
    recentJobs: [
      { id: 1, title: 'Summer Catalog', client: 'Fashion Brand X', date: '2024-05-20', status: 'Completed' },
      { id: 2, title: 'Annual Report', client: 'Corp Finance', date: '2024-05-21', status: 'In Progress' },
      { id: 3, title: 'Business Cards', client: 'Local Shop', date: '2024-05-22', status: 'Pending' },
    ]
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  useEffect(() => {
    if (token) {
      fetchReportData();
      if (isAdmin || isPM) {
        fetchUsers();
        fetchClients();
        fetchTeams();
      }
    } else {
      setReportData(mockData);
    }
  }, [timeRange, selectedUser, token, startDate, endDate]);

  useEffect(() => {
    const now = new Date();
    const start = new Date(now);
    if (timeRange === 'week') {
      start.setDate(now.getDate() - 7);
    } else if (timeRange === 'month') {
      start.setMonth(now.getMonth() - 1);
    } else if (timeRange === 'quarter') {
      start.setMonth(now.getMonth() - 3);
    } else if (timeRange === 'year') {
      start.setFullYear(now.getFullYear() - 1);
    }
    setStartDate(start);
    setEndDate(now);
  }, [timeRange]);



  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/admin/clients', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data || []);
    } catch (err) {
      console.error('Failed to fetch clients');
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await api.get('/admin/teams', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch teams');
      setTeams([]);
    }
  };

  const fetchCompletedProjects = async () => {
    if (!token) return;
    setCompletedProjLoading(true);
    try {
      const response = await api.get('/pm/projects/completed', {
        headers: { Authorization: `Bearer ${token}` }
      });
      let projects = Array.isArray(response.data) ? response.data : [];
      
      // Filter by date range - add 1 day to toDate to include entire end date
      const fromDate = completedProjFrom ? new Date(completedProjFrom) : null;
      const toDate = completedProjTo ? new Date(completedProjTo) : null;
      if (toDate) {
        toDate.setDate(toDate.getDate() + 1);
      }
      
      if (fromDate || toDate) {
        projects = projects.filter(p => {
          const archivedAt = p.archived_at ? new Date(p.archived_at) : null;
          if (!archivedAt) return false;
          if (fromDate && archivedAt < fromDate) return false;
          if (toDate && archivedAt > toDate) return false;
          return true;
        });
      }
      
      // Filter by clients
      if (completedProjClients && completedProjClients.length > 0) {
        projects = projects.filter(p => completedProjClients.includes(p.client));
      }
      
      setCompletedProjData(projects);
    } catch (err) {
      console.error('Failed to fetch completed projects', err);
      setCompletedProjData([]);
    } finally {
      setCompletedProjLoading(false);
    }
  };

  const handleExportCompletedProjects = () => {
    if (!completedProjData || completedProjData.length === 0) {
      alert('No completed projects data to export');
      return;
    }
    const rows = completedProjData.map(p => ({
      ...p,
      archived_at: p.archived_at ? formatDateIST(p.archived_at) : '',
      created_at: p.created_at ? formatDateIST(p.created_at) : ''
    }));
    const data = formatDataForExport(rows, {
      job_code: 'Job Number',
      title: 'Project Title',
      client: 'Client',
      archived_at: 'Completed Date',
      created_at: 'Created Date',
      complexity: 'Complexity',
      status: 'Status'
    });
    exportToExcel(data, `completed_projects_${completedProjFrom}_to_${completedProjTo}.csv`);
  };

  const fetchActiveProjects = async () => {
    if (!token) return;
    setActiveProjLoading(true);
    try {
      const params = {};
      if (activeProjClients && activeProjClients.length > 0) {
        params.clients = activeProjClients.join(',');
      }
      const response = await api.get('/reports/active-projects', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      const projects = Array.isArray(response.data) ? response.data : [];
      setActiveProjData(projects);
    } catch (err) {
      console.error('Failed to fetch active projects', err);
      setActiveProjData([]);
    } finally {
      setActiveProjLoading(false);
    }
  };

  const handleExportActiveProjects = () => {
    if (!activeProjData || activeProjData.length === 0) {
      alert('No active projects data to export');
      return;
    }
    const rows = activeProjData.map(p => ({
      job_code: p.job_code || '',
      title: p.title || '',
      client: p.client || '',
      complexity: p.complexity || '',
      current_stage: p.current_stage || '',
      current_stage_status: p.current_stage_status || '',
      current_stage_team: p.current_stage_team || '',
      completion_percentage: p.completion_percentage != null ? `${p.completion_percentage}%` : '',
      total_tasks: p.total_tasks || 0,
      completed_tasks: p.completed_tasks || 0,
      pending_tasks: p.pending_tasks || 0,
      status: p.status || 'active',
      created_at: p.created_at ? formatDateIST(p.created_at) : ''
    }));
    const data = formatDataForExport(rows, {
      job_code: 'Job Number',
      title: 'Project Title',
      client: 'Client',
      complexity: 'Complexity',
      current_stage: 'Current Stage',
      current_stage_status: 'Stage Status',
      current_stage_team: 'Stage Team',
      completion_percentage: 'Completion %',
      total_tasks: 'Total Tasks',
      completed_tasks: 'Completed Tasks',
      pending_tasks: 'Pending Tasks',
      status: 'Status',
      created_at: 'Created Date'
    });
    exportToExcel(data, 'active_projects.csv');
  };

  const fetchTeamUtilization = async () => {
    if (!token) return;
    try {
      setTeamUtilLoading(true);
      // Add 1 day to endDate to include the entire end date
      const toDate = new Date(teamUtilTo);
      toDate.setDate(toDate.getDate() + 1);
      
      const response = await api.get('/reports/team-utilization', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: teamUtilFrom,
          endDate: toDate.toISOString().split('T')[0]
        }
      });
      setTeamData(response.data || []);
    } catch (err) {
      console.error('Failed to fetch team utilization', err);
      setTeamData([]);
    } finally {
      setTeamUtilLoading(false);
    }
  };

  const fetchReportData = async () => {
    try {
      let url = `/reports/summary?range=${timeRange}`;
      if (selectedUser) {
        url += `&userId=${selectedUser}`;
      }
      
      const response = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Map backend summary to chart data
      const summary = response.data;
      const mappedData = {
        ...mockData,
        jobsByStatus: [
          { name: 'Completed', value: summary.completed || 0 },
          { name: 'In Progress', value: summary.in_progress || 0 },
          { name: 'Pending', value: (summary.total - summary.completed - summary.in_progress) || 0 },
        ]
      };
      setReportData(mappedData);
    } catch (err) {
      console.error('Failed to fetch report data');
      setReportData(mockData);
    }
  };

  const handleExport = async () => {
    try {
      if (!token) {
        alert('Please login to export reports.');
        return;
      }

      const params = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };
      if (selectedUser) params.userId = selectedUser;

      if (exportType === 'team-utilization') {
        handleExportTeamData();
        return;
      }

      if (exportType === 'time-tracking') {
        const resp = await api.get('/reports/time-tracking', {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        const rows = Array.isArray(resp.data) ? resp.data : [];
        const mapped = rows.map(row => ({
          ...row,
          shift_start: row.shift_start ? formatDateTimeIST(row.shift_start) : '',
          shift_end: row.shift_end ? formatDateTimeIST(row.shift_end) : '',
        }));
        const data = formatDataForExport(mapped, {
          date: 'Date',
          user_name: 'User',
          shift_start: 'Shift Start',
          shift_end: 'Shift End',
          total_shift_hours: 'Total Shift Hours',
          job_hours: 'Job Hours',
          break_hours: 'Break Hours',
          idle_hours: 'Idle Hours',
        });
        exportToExcel(data, `time_tracking_${toDateInput(new Date())}.csv`);
        return;
      }

      if (exportType === 'activity-log') {
        const resp = await api.get('/reports/activity-log', {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        const rows = Array.isArray(resp.data) ? resp.data : [];
        const mapped = rows.map(row => ({
          ...row,
          ts: row.ts ? formatDateTimeIST(row.ts) : ''
        }));
        const data = formatDataForExport(mapped, {
          ts: 'Timestamp (IST)',
          user_name: 'User',
          event: 'Event',
          description: 'Description',
          notes: 'Notes',
          task_id: 'Task ID',
          project_id: 'Project ID'
        });
        exportToExcel(data, `activity_log_${toDateInput(new Date())}.csv`);
        return;
      }

      alert('Unknown export type');
    } catch (err) {
      console.error('Failed to export report', err);
      alert('Failed to export report');
    }
  };

  const handleExportTeamData = () => {
    if (teamData.length === 0) {
      alert('No team data to export');
      return;
    }

    const data = formatDataForExport(teamData, {
      'team_name': 'Team Name',
      'team_type': 'Team Type',
      'member_count': 'Members',
      'total_shift_hours': 'Total Shift Hours',
      'job_hours': 'Job Hours',
      'break_hours': 'Break Hours',
      'break_lunch': 'Break Lunch',
      'break_snacks': 'Break Snacks',
      'break_dinner': 'Break Dinner',
      'idle_hours': 'Idle Hours',
      'idle_meeting': 'Idle Meeting',
      'idle_call': 'Idle Call',
      'idle_training': 'Idle Training',
      'idle_no_jobs': 'No Jobs Hrs',
      'non_tracking_hours': 'Non Tracking Hrs',
      'shift_count': 'Shift Count',
      'tasks_completed': 'Tasks Completed',
      'utilization_percentage': 'Utilization %',
      'efficiency_percentage': 'Efficiency %'
    });

    exportToExcel(data, `team_utilization_${teamUtilFrom}_to_${teamUtilTo}.csv`);
  };

  // Export: User Task Summary by Job Type
  const handleExportUserTaskSummary = async () => {
    if (!token) {
      alert('Please login to export reports.');
      return;
    }
    if (!taskSummaryFrom || !taskSummaryTo) {
      alert('Please select both From and To dates for the task summary.');
      return;
    }

    setTaskSummaryLoading(true);
    try {
      const params = {
        startDate: taskSummaryFrom,
        endDate: taskSummaryTo
      };

      const resp = await api.get('/reports/user-task-summary', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      const rows = Array.isArray(resp.data) ? resp.data : [];
      if (!rows.length) {
        alert('No user task summary data found for the selected range.');
      } else {
        const jobTypeKeys = Array.from(
          new Set(
            rows.flatMap(row =>
              Object.keys(row || {}).filter(k => k.startsWith('job_type_'))
            )
          )
        );

        const headerMap = {
          role: 'Role',
          team: 'Team',
          user_name: 'User',
          total_projects: 'Total Projects',
          total_tasks: 'Total Tasks'
        };

        jobTypeKeys.forEach(key => {
          const label = key.replace(/^job_type_/, '').replace(/_/g, ' ');
          headerMap[key] = `Job Type: ${label}`;
        });

        const data = formatDataForExport(rows, headerMap);
        exportToExcel(
          data,
          `user_task_summary_by_Job_Type_${taskSummaryFrom}_to_${taskSummaryTo}.csv`
        );
      }
    } catch (err) {
      console.error('Failed to export user task summary', err);
      alert('Failed to export user task summary');
    } finally {
      setTaskSummaryLoading(false);
    }
  };

  // Export: User Task Details (completed tasks with timing & errors)
  const handleExportUserTaskDetails = async () => {
    if (!token) {
      alert('Please login to export reports.');
      return;
    }
    if (!taskDetailsFrom || !taskDetailsTo) {
      alert('Please select both From and To dates for the task details.');
      return;
    }

    try {
      const params = {
        startDate: taskDetailsFrom,
        endDate: taskDetailsTo
      };
      if (selectedUser) {
        params.userId = selectedUser;
      }

      const resp = await api.get('/reports/user-task-details', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      let rows = Array.isArray(resp.data) ? resp.data : [];
      if (!rows.length) {
        alert('No user task details found for the selected filters.');
        return;
      }

      // Sort by user then by end time for nicer grouping
      rows = rows.slice().sort((a, b) => {
        const ua = (a.user_name || '').localeCompare(b.user_name || '');
        if (ua !== 0) return ua;
        const ea = a.end_time || '';
        const eb = b.end_time || '';
        return String(ea).localeCompare(String(eb));
      });

      // Derive a Task Name from the round field when needed
      // and prepare for per-error-category flags.
      const normalizedRows = rows.map(row => {
        let taskName = row.task_name;
        const roundValue = row.round != null && row.round !== '' ? row.round : null;

        if (!taskName && roundValue !== null) {
          const roundStr = String(roundValue).trim();
          if (roundStr) {
            const upper = roundStr.toUpperCase();
            taskName = upper.startsWith('R') ? upper : `R${upper}`;
          }
        }

        return taskName
          ? { ...row, task_name: taskName }
          : row;
      });

      // Collect all error categories across rows. The backend should
      // provide an array field like `error_categories` on each row,
      // e.g. ['text', 'image'].
      const allErrorCategories = new Set();
      normalizedRows.forEach(row => {
        const categories = Array.isArray(row.error_categories) ? row.error_categories : [];
        categories.forEach(cat => {
          if (cat != null && cat !== '') {
            allErrorCategories.add(String(cat));
          }
        });
      });

      const errorCategoryLabels = Array.from(allErrorCategories).sort();

      // For each row, add numeric columns (0/1) for each error category.
      const finalRows = normalizedRows.map(row => {
        const categories = Array.isArray(row.error_categories) ? row.error_categories : [];
        const categorySet = new Set(categories.map(c => String(c)));
        const extra = {};

        errorCategoryLabels.forEach(cat => {
          const slug = cat
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');
          const key = `error_${slug}`;
          extra[key] = categorySet.has(cat) ? 1 : 0;
        });

        return { ...row, ...extra };
      });

      const headerMap = {
        job_code: 'Job Number',
        job_type: 'Job Type',
        project_title: 'Project Title',
        client_name: 'Customer Name',
        user_name: 'User',
        role: 'Role',
        team: 'Team',
        complexity: 'Complexity',
        complexity_minutes: 'Complexity Time',
        round: 'Round',
        task_name: 'Task Name',
        start_time: 'Start Time',
        end_time: 'End Time',
        duration_minutes: 'Task Time (H.MM)',
        exceed_time: 'Exceed Time (H.MM)',
        internal_errors: 'Internal Errors',
        external_errors: 'External Errors',
        total_errors: 'Total Errors'
      };

      // Append one column per error category after Total Errors.
      errorCategoryLabels.forEach(cat => {
        const slug = cat
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');
        const key = `error_${slug}`;
        headerMap[key] = `${cat} Error`;
      });

      // Format duration_minutes as H.MM and compute exceed_time before export
      const exportRows = finalRows.map(row => {
        const next = { ...row };
        const mins = next.duration_minutes != null && next.duration_minutes !== ''
          ? parseFloat(next.duration_minutes)
          : NaN;
        const complexMins = next.complexity_minutes != null && next.complexity_minutes !== ''
          ? parseFloat(next.complexity_minutes)
          : NaN;
        // Compute exceed time from raw minutes before formatting
        if (isFinite(mins) && isFinite(complexMins) && mins > complexMins) {
          const exceedMins = mins - complexMins;
          const eh = Math.floor(exceedMins / 60);
          const em = Math.round(exceedMins % 60);
          next.exceed_time = `${eh}.${String(em).padStart(2, '0')}`;
        } else {
          next.exceed_time = '';
        }
        if (isFinite(mins) && mins >= 0) {
          const h = Math.floor(mins / 60);
          const m = Math.round(mins % 60);
          next.duration_minutes = `${h}.${String(m).padStart(2, '0')}`;
        }
        return next;
      });

      const data = formatDataForExport(exportRows, headerMap);
      exportToExcel(
        data,
        `user_completed_task_details_${taskDetailsFrom}_to_${taskDetailsTo}.csv`
      );
    } catch (err) {
      console.error('Failed to export user task details', err);
      alert('Failed to export user task details');
    }
  };

  // Export: Today Job Information
  const handleExportTodayJobInfo = async () => {
    if (!token) {
      alert('Please login to export reports.');
      return;
    }

    setTodayJobInfoLoading(true);
    try {
      const resp = await api.get('/reports/today-job-info', {
        headers: { Authorization: `Bearer ${token}` },
        params: todayJobInfoDate ? { date: todayJobInfoDate } : undefined,
      });

      const data = resp.data;
      if (!data || data.total === 0) {
        alert('No projects booked on the selected date.');
        setTodayJobInfoLoading(false);
        return;
      }

      // Build CSV content manually for custom layout
      const lines = [];

      // Row 1: Title
      lines.push(['Daily Job Information']);

      // Row 2: Date
      lines.push(['Date', data.date || new Date().toLocaleDateString()]);

      // Row 3: Empty row
      lines.push([]);

      // Row 4: Summary headers
      lines.push(['', 'Total', 'Completed', 'Completed %', 'Active', 'Active %']);

      // Row 5: Summary values
      lines.push(['Projects', data.total, data.completed, `${data.completed_pct}%`, data.active, `${data.active_pct}%`]);

      // Row 6: Empty row
      lines.push([]);

      // Section: Active Projects by Stage
      lines.push(['Active Projects by Stage']);
      const stageCounts = data.stage_counts || {};
      const stageNames = Object.keys(stageCounts).sort();
      if (stageNames.length > 0) {
        lines.push(['Stage', 'Count']);
        stageNames.forEach(stage => {
          lines.push([stage, stageCounts[stage]]);
        });
      } else {
        lines.push(['No active projects']);
      }

      // Row: Empty
      lines.push([]);

      // Section: By Job Nature
      lines.push(['Projects by Job Nature']);
      const jobNatureCompleted = data.job_nature_completed || {};
      const jobNatureActive = data.job_nature_active || {};
      const allJobNatures = Array.from(new Set([
        ...Object.keys(jobNatureCompleted),
        ...Object.keys(jobNatureActive)
      ])).sort();

      if (allJobNatures.length > 0) {
        lines.push(['Job Nature', 'Completed', 'Active', 'Total']);
        allJobNatures.forEach(nature => {
          const completed = jobNatureCompleted[nature] || 0;
          const active = jobNatureActive[nature] || 0;
          lines.push([nature, completed, active, completed + active]);
        });
      }

      // Row: Empty
      lines.push([]);

      // Section: By Job Type
      lines.push(['Projects by Job Type']);
      const jobTypeCompleted = data.job_type_completed || {};
      const jobTypeActive = data.job_type_active || {};
      const allJobTypes = Array.from(new Set([
        ...Object.keys(jobTypeCompleted),
        ...Object.keys(jobTypeActive)
      ])).sort();

      if (allJobTypes.length > 0) {
        lines.push(['Job Type', 'Completed', 'Active', 'Total']);
        allJobTypes.forEach(type => {
          const completed = jobTypeCompleted[type] || 0;
          const active = jobTypeActive[type] || 0;
          lines.push([type, completed, active, completed + active]);
        });
      }

      // Convert to CSV
      const csvContent = lines.map(row => {
        return row.map(cell => {
          const value = cell === null || cell === undefined ? '' : String(cell);
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',');
      }).join('\n');

      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `daily_job_info_${data.date || 'report'}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      console.error('Failed to export today job info', err);
      alert('Failed to export today job info');
    } finally {
      setTodayJobInfoLoading(false);
    }
  };

  const handleExportErrorReport = async () => {
    if (!token) { alert('Please login to export reports.'); return; }

    const jobCodes = Array.from(new Set(
      (errorReportJobCodes || '').split(/[\s,;\n\r\t]+/).map(s => s.trim()).filter(Boolean)
    ));

    const params = { type: errorReportType };
    let fileSuffix;
    if (errorReportMode === 'job') {
      if (jobCodes.length === 0) { alert('Enter at least one job code.'); return; }
      params.job_code = jobCodes.join(',');
      fileSuffix = jobCodes.length === 1 ? jobCodes[0] : `${jobCodes.length}_jobs`;
    } else {
      if (!errorReportFrom || !errorReportTo) { alert('Please select a date range.'); return; }
      params.from = errorReportFrom;
      params.to = errorReportTo;
      if (errorReportClients && errorReportClients.length > 0) {
        params.client = errorReportClients.join(',');
      }
      fileSuffix = `${errorReportFrom}_to_${errorReportTo}`;
    }

    setErrorReportLoading(true);
    try {
      const resp = await api.get('/reports/error-details', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      const rows = Array.isArray(resp.data) ? resp.data : [];
      if (rows.length === 0) {
        alert('No records found for the selected filters.');
        return;
      }
      const typeLabel = errorReportType === 'external' ? 'External' : errorReportType === 'internal' ? 'Internal' : 'All';
      const headerMap = {
        sno: 'S.No',
        client_name: 'Customer Name',
        job_code: 'Job No',
        job_name: 'Job Name',
        revision: 'Revision',
        reason: 'Reason',
        total_timing: 'Total Timing',
      };
      const exportRows = rows.map((r, idx) => ({
        sno: idx + 1,
        client_name: r.client_name || '',
        job_code: r.job_code || '',
        job_name: r.job_name || '',
        revision: r.revision || '',
        reason: r.reason || '',
        total_timing: r.total_timing || '',
      }));
      const data = formatDataForExport(exportRows, headerMap);
      exportToExcel(data, `${typeLabel}_Rejection_Report_${fileSuffix}.csv`);
    } catch (err) {
      console.error('Failed to export error report', err);
      alert('Failed to export error report');
    } finally {
      setErrorReportLoading(false);
    }
  };

  // Aggregate raw time-tracking summary rows by user so the
  // "User Time Tracking Summary" view shows one entry per user
  // instead of one per date.
  const aggregatedUserSummary = Array.isArray(userSummary) && userSummary.length > 0
    ? Object.values(
        userSummary.reduce((acc, row) => {
          const key = row.user_id || row.user_name || 'unknown';
          if (!acc[key]) {
            acc[key] = {
              ...row,
              total_shift_hours: 0,
              job_hours: 0,
              break_hours: 0,
              break_lunch: 0,
              break_snacks: 0,
              break_dinner: 0,
              idle_meeting: 0,
              idle_call: 0,
              idle_training: 0,
              idle_no_jobs: 0,
              idle_hours: 0,
              non_tracking_hours: 0,
              tasks_done: 0,
              projects_done: 0
            };
          }

          acc[key].total_shift_hours += row.total_shift_hours || 0;
          acc[key].job_hours += row.job_hours || 0;
          acc[key].break_hours += row.break_hours || 0;
          acc[key].break_lunch += row.break_lunch || 0;
          acc[key].break_snacks += row.break_snacks || 0;
          acc[key].break_dinner += row.break_dinner || 0;
          acc[key].idle_meeting += row.idle_meeting || 0;
          acc[key].idle_call += row.idle_call || 0;
          acc[key].idle_training += row.idle_training || 0;
          acc[key].idle_no_jobs += row.idle_no_jobs || 0;
          acc[key].idle_hours += row.idle_hours || 0;
          acc[key].non_tracking_hours += row.non_tracking_hours || 0;
          acc[key].tasks_done += row.tasks_done || 0;
          acc[key].projects_done += row.projects_done || 0;

          return acc;
        }, {})
      ).sort((a, b) => (a.user_name || '').localeCompare(b.user_name || ''))
    : [];

  if (!reportData) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6">
            
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Reports & Analytics</h1>
        
        
      </div>

      
      
      {(isAdmin || isPM || isLead) && (
        <div className="mb-6">
          {/* Forms Grid */}
          
            <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            { (isAdmin)  && ( 
              <>
              {/* User Task Summary by Job Type (moved above) */}
                <div className="bg-gradient-to-br from-indigo-300 to-gray-300 rounded-xl p-4">
                  <form className="px-6 py-4">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">User Task Summary by Job Type</h2>
                    <div className="flex flex-col gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-2">
                          Export how many tasks each user completed, grouped by job type, for a selected date range.
                        </p>
                        <ul className="text-xs text-gray-600 list-disc list-inside mb-2">
                          <li>Total projects and tasks completed per user</li>
                          <li>Separate columns for each job type handled</li>
                          <li>Useful to compare workload across users and job types</li>
                        </ul>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                            <input
                              type="date"
                              value={taskSummaryFrom}
                              onChange={e => setTaskSummaryFrom(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                            <input
                              type="date"
                              value={taskSummaryTo}
                              onChange={e => setTaskSummaryTo(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              required
                            />
                          </div>
                        </div>
                        <div className="flex gap-3 pt-2">
                          <button
                            type="button"
                            onClick={handleExportUserTaskSummary}
                            disabled={taskSummaryLoading}
                            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors disabled:opacity-60"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            {taskSummaryLoading ? 'Exporting…' : 'User Task Summary'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

              {/* User Task Completed Details (moved above) */}
                <div className="bg-gradient-to-br from-purple-300 to-gray-300 rounded-xl p-4">
                  <form className="px-6 py-4">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">User Completed Task Details</h2>
                    <div className="flex flex-col gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-2">
                          Export a detailed line-by-line list of all completed tasks in a date range.
                        </p>
                        <ul className="text-xs text-gray-600 list-disc list-inside mb-2">
                          <li>Includes job number, project, client, task name and round</li>
                          <li>Start/end time, task minutes and user/role/team</li>
                          <li>Breakdown of internal/external errors and error categories</li>
                        </ul>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                            <input
                              type="date"
                              value={taskDetailsFrom}
                              onChange={e => setTaskDetailsFrom(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                            <input
                              type="date"
                              value={taskDetailsTo}
                              onChange={e => setTaskDetailsTo(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              required
                            />
                          </div>
                        </div>
                        <div className="flex gap-3 pt-2">
                          <button
                            type="button"
                            onClick={handleExportUserTaskDetails}
                            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium transition-colors"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Export Completed Task Details
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

              {/* Daily project information */}
                <div className="bg-gradient-to-br from-green-300 to-gray-300 rounded-xl p-4">
                  <form className="px-6 py-4">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Daily Job Information</h2>
                    <div className="flex flex-col gap-4">
                      <p className="text-sm text-gray-600">
                        Select a date and export that day's booking projects summary:
                      </p>
                      <ul className="text-xs text-gray-600 list-disc list-inside">
                        <li>Total, Completed, Active project counts with percentages</li>
                        <li>Active projects by current workflow stage</li>
                        <li>Breakdown by Job Nature and Job Type</li>
                      </ul>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                          <input
                            type="date"
                            value={todayJobInfoDate}
                            onChange={e => setTodayJobInfoDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            required
                          />
                        </div>
                      </div>
                      <div className="flex gap-3 pt-2">
                        <button
                          type="button"
                          onClick={handleExportTodayJobInfo}
                          disabled={todayJobInfoLoading}
                          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors disabled:opacity-60"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          {todayJobInfoLoading ? 'Exporting…' : 'Export Daily Report'}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </>
            )}

          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* User Time Tracking Summary Section */}
            {(isAdmin) && (
            <div className="bg-gradient-to-br from-gray-300 to-gray-400 rounded-xl p-4">
              <form className="px-6 py-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">User Time Tracking Summary</h2>
                <div className="flex flex-col gap-4">
                  <p className="text-sm text-gray-600 mb-2">
                    View and export consolidated shift, job, break and idle hours per user for a selected period.
                  </p>
                  <ul className="text-xs text-gray-600 list-disc list-inside mb-2">
                    <li>Total shift hours, job hours, break and idle time</li>
                    <li>Breakdown of lunch/snacks/dinner and idle reasons</li>
                    <li>Counts of tasks done and projects completed per user</li>
                  </ul>
                  {/* Users field - like TimeSheet, but searchable */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Users</label>
                    <div className="relative min-w-[250px]" style={{ zIndex: 20 }}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white flex items-center justify-between"
                        onClick={() => setShowUserDropdown(v => !v)}
                        title="Users"
                      >
                        <span className="truncate">
                          {userSummaryUser && userSummaryUser.length > 0
                            ? (() => {
                                const selectedId = userSummaryUser[0];
                                const match = users.find(u => resolveUserIdentifier(u) === selectedId);
                                return match?.name || 'Selected User';
                              })()
                            : (isAdmin ? 'All Users' : 'All Team Members')}
                        </span>
                        <svg className="w-4 h-4 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>

                      {showUserDropdown && (
                        <div className="absolute left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto z-30">
                          <div className="p-2 border-b border-gray-100">
                            <input
                              type="text"
                              placeholder="Search..."
                              value={userDropdownSearch}
                              onChange={e => setUserDropdownSearch(e.target.value)}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                              autoFocus
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            <button
                              type="button"
                              onClick={() => {
                                setUserSummaryUser([]);
                                setUserDropdownSearch('');
                                setShowUserDropdown(false);
                              }}
                              className="w-full text-left px-3 py-1 text-sm hover:bg-gray-50"
                            >
                              {isAdmin ? 'All Users' : 'All Team Members'}
                            </button>
                            {users
                              .filter(u =>
                                !userDropdownSearch ||
                                (u.name || '').toLowerCase().includes(userDropdownSearch.toLowerCase())
                              )
                              .map(u => {
                                const userId = resolveUserIdentifier(u);
                                const isSelected =
                                  userSummaryUser &&
                                  userSummaryUser.length > 0 &&
                                  userSummaryUser[0] === userId;
                                return (
                                  <button
                                    key={userId || u.name}
                                    type="button"
                                    onClick={() => {
                                      if (!userId) return;
                                      setUserSummaryUser([userId]);
                                      setShowUserDropdown(false);
                                    }}
                                    className={`w-full text-left px-3 py-1 text-sm hover:bg-gray-50 ${
                                      isSelected ? 'bg-blue-50 font-medium' : ''
                                    }`}
                                  >
                                    {u.name}
                                  </button>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Date range fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">From <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      value={userSummaryFrom}
                      onChange={e => setUserSummaryFrom(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">To <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      value={userSummaryTo}
                      onChange={e => setUserSummaryTo(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      required
                    />
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setUserSummaryTriggered(t => !t); fetchUserSummary(); setShowUserSummaryModal(true); }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                  >
                    View
                  </button>
                  <button
                    type="button"
                    onClick={handleExportUserSummary}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Detailed Summary
                  </button>
                </div>
              </form>
            </div>
            )}
          
            {/* Team Utilization Section */}
            {(isAdmin) && (
            <div className="bg-gradient-to-br from-blue-300 to-blue-400 rounded-xl p-4">
              <form className="px-6 py-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Team Utilization & Efficiency</h2>
                <div className="flex flex-col gap-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Analyze how each team spends its time and how efficiently it converts shift hours into completed tasks.
                  </p>
                  <ul className="text-xs text-gray-600 list-disc list-inside mb-2">
                    <li>Total shift, job, break and idle hours per team</li>
                    <li>Member count and tasks completed</li>
                    <li>Utilization % (job time vs shift) and efficiency %</li>
                  </ul>
                  {/* Teams field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Teams <span className="text-red-500">*</span></label>
                    <div className="relative min-w-[250px]" style={{ zIndex: 19 }}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white flex items-center justify-between"
                        onClick={() => setShowTeamDropdown(v => !v)}
                        title="Teams"
                      >
                        <span className="truncate">
                          {teamUtilTeams && teamUtilTeams.length > 0
                            ? teamUtilTeams.length === teams.length
                              ? 'All Teams'
                              : teams
                                  .filter(t => teamUtilTeams.includes(t.id || t._id))
                                  .map(t => t.name)
                                  .join(', ')
                            : 'All Teams'}
                        </span>
                        <svg className="w-4 h-4 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {showTeamDropdown && (
                        <div className="absolute left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto z-30">
                          <div className="p-2 border-b border-gray-100">
                            <input
                              type="text"
                              placeholder="Search..."
                              value={teamDropdownSearch}
                              onChange={e => setTeamDropdownSearch(e.target.value)}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                              autoFocus
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            <label className="flex items-center px-2 py-1 cursor-pointer hover:bg-gray-50 rounded">
                              <input
                                type="checkbox"
                                checked={teamUtilTeams && teamUtilTeams.length === teams.length && teams.length > 0}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setTeamUtilTeams(teams.map(t => t.id || t._id));
                                  } else {
                                    setTeamUtilTeams([]);
                                  }
                                }}
                                className="mr-2"
                              />
                              <span className="text-sm">Select All</span>
                            </label>
                            {teams
                              .filter(t =>
                                !teamDropdownSearch ||
                                t.name.toLowerCase().includes(teamDropdownSearch.toLowerCase())
                              )
                              .map(t => (
                                <label key={t.id || t._id} className="flex items-center px-2 py-1 cursor-pointer hover:bg-gray-50 rounded">
                                  <input
                                    type="checkbox"
                                    checked={teamUtilTeams && teamUtilTeams.includes(t.id || t._id)}
                                    onChange={e => {
                                      const teamId = t.id || t._id;
                                      if (e.target.checked) {
                                        setTeamUtilTeams(prev => Array.from(new Set([...(prev || []), teamId])));
                                      } else {
                                        setTeamUtilTeams(prev => (prev || []).filter(id => id !== teamId));
                                      }
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">{t.name}</span>
                                </label>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Date range fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">From <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        value={teamUtilFrom}
                        onChange={e => setTeamUtilFrom(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">To <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        value={teamUtilTo}
                        onChange={e => setTeamUtilTo(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        required
                      />
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => { fetchTeamUtilization(); setShowTeamUtilModal(true); }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                    >
                      View
                    </button>
                    <button
                      type="button"
                      onClick={handleExportTeamData}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Detailed Report
                    </button>
                  </div>
                </div>
              </form>
            </div>
            )}

              {/* Completed Projects Report Form */}
            {(isAdmin || isPM) && (
            <div className="bg-gradient-to-br from-green-300 to-gray-400 rounded-xl p-4">
              <form className="px-6 py-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Completed Projects Report</h2>
                <div className="flex flex-col gap-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Export all projects that were completed in the selected date range, with key metadata.
                  </p>
                  <ul className="text-xs text-gray-600 list-disc list-inside mb-2">
                    <li>Job number, project title, client and complexity</li>
                    <li>Completed date (archive date) and created date</li>
                    <li>Optional filter by one or more clients</li>
                  </ul>
                  {/* Clients field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Clients</label>
                    <div className="relative min-w-[250px]" style={{ zIndex: 19 }}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white flex items-center justify-between"
                        onClick={() => setShowClientDropdown(v => !v)}
                        title="Clients"
                      >
                        <span className="truncate">
                          {completedProjClients && completedProjClients.length > 0
                            ? completedProjClients.length === clients.length
                              ? 'All Clients'
                              : completedProjClients.join(', ')
                            : 'All Clients'}
                        </span>
                        <svg className="w-4 h-4 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {showClientDropdown && (
                        <div className="absolute left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto z-30">
                          <div className="p-2 border-b border-gray-100">
                            <input
                              type="text"
                              placeholder="Search..."
                              value={clientDropdownSearch}
                              onChange={e => setClientDropdownSearch(e.target.value)}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                              autoFocus
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            <label className="flex items-center px-2 py-1 cursor-pointer hover:bg-gray-50 rounded">
                              <input
                                type="checkbox"
                                checked={completedProjClients && completedProjClients.length === clients.length && clients.length > 0}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setCompletedProjClients(clients.map(c => c.name));
                                  } else {
                                    setCompletedProjClients([]);
                                  }
                                }}
                                className="mr-2"
                              />
                              <span className="text-sm">Select All</span>
                            </label>
                            {clients
                              .filter(c =>
                                !clientDropdownSearch ||
                                c.name.toLowerCase().includes(clientDropdownSearch.toLowerCase())
                              )
                              .map(c => (
                                <label key={c.name} className="flex items-center px-2 py-1 cursor-pointer hover:bg-gray-50 rounded">
                                  <input
                                    type="checkbox"
                                    checked={completedProjClients && completedProjClients.includes(c.name)}
                                    onChange={e => {
                                      if (e.target.checked) {
                                        setCompletedProjClients(prev => Array.from(new Set([...(prev || []), c.name])));
                                      } else {
                                        setCompletedProjClients(prev => (prev || []).filter(name => name !== c.name));
                                      }
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">{c.name}</span>
                                </label>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Date range fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">From <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        value={completedProjFrom}
                        onChange={e => setCompletedProjFrom(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">To <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        value={completedProjTo}
                        onChange={e => setCompletedProjTo(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        required
                      />
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => { fetchCompletedProjects(); setShowCompletedProjModal(true); }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                    >
                      View
                    </button>
                    <button
                      type="button"
                      onClick={handleExportCompletedProjects}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Detailed Report
                    </button>
                  </div>
                </div>
              </form>
            </div>
            )}

              {/* Active Projects Report Form */}
            {(isAdmin || isPM) && (
            <div className="bg-gradient-to-br from-yellow-300 to-gray-400 rounded-xl p-4">
              <form className="px-6 py-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Active Projects Report</h2>
                <div className="flex flex-col gap-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Export all currently active projects for the selected clients, regardless of created date.
                  </p>
                  <ul className="text-xs text-gray-600 list-disc list-inside mb-2">
                    <li>Job number, project title, client and complexity</li>
                    <li>Current status and created date</li>
                    <li>Useful to review live workload by customer</li>
                  </ul>
                  {/* Clients field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Clients</label>
                    <div className="relative min-w-[250px]" style={{ zIndex: 18 }}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white flex items-center justify-between"
                        onClick={() => setShowActiveClientDropdown(v => !v)}
                        title="Clients"
                      >
                        <span className="truncate">
                          {activeProjClients && activeProjClients.length > 0
                            ? activeProjClients.length === clients.length
                              ? 'All Clients'
                              : activeProjClients.join(', ')
                            : 'All Clients'}
                        </span>
                        <svg className="w-4 h-4 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {showActiveClientDropdown && (
                        <div className="absolute left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto z-30">
                          <div className="p-2 border-b border-gray-100">
                            <input
                              type="text"
                              placeholder="Search..."
                              value={activeClientDropdownSearch}
                              onChange={e => setActiveClientDropdownSearch(e.target.value)}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                              autoFocus
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            <label className="flex items-center px-2 py-1 cursor-pointer hover:bg-gray-50 rounded">
                              <input
                                type="checkbox"
                                checked={activeProjClients && activeProjClients.length === clients.length && clients.length > 0}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setActiveProjClients(clients.map(c => c.name));
                                  } else {
                                    setActiveProjClients([]);
                                  }
                                }}
                                className="mr-2"
                              />
                              <span className="text-sm">Select All</span>
                            </label>
                            {clients
                              .filter(c =>
                                !activeClientDropdownSearch ||
                                c.name.toLowerCase().includes(activeClientDropdownSearch.toLowerCase())
                              )
                              .map(c => (
                                <label key={c.name} className="flex items-center px-2 py-1 cursor-pointer hover:bg-gray-50 rounded">
                                  <input
                                    type="checkbox"
                                    checked={activeProjClients && activeProjClients.includes(c.name)}
                                    onChange={e => {
                                      if (e.target.checked) {
                                        setActiveProjClients(prev => Array.from(new Set([...(prev || []), c.name])));
                                      } else {
                                        setActiveProjClients(prev => (prev || []).filter(name => name !== c.name));
                                      }
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">{c.name}</span>
                                </label>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => { fetchActiveProjects(); setShowActiveProjModal(true); }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                    >
                      View
                    </button>
                    <button
                      type="button"
                      onClick={handleExportActiveProjects}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Detailed Report
                    </button>
                  </div>
                </div>
              </form>
            </div>
            )}

            {/* (User Task Reports section was split into two separate cards above) */}

              {/* Error Rejection Report (External / Internal) */}
            {(isAdmin || isPM) && (
            <div className="bg-gradient-to-br from-red-200 to-gray-400 rounded-xl p-4">
              <form className="px-6 py-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Rejection Report</h2>
                <div className="flex flex-col gap-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Download external or internal rejection records for any date range. Columns: S.No, Customer Name, Job No, Job Name, Revision, Reason, Total Timing.
                  </p>

                  {/* Report Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                    <div className="flex gap-4">
                      {[['external', 'External Rejections'], ['internal', 'Internal Rejections'], ['all', 'All Rejections']].map(([val, label]) => (
                        <label key={val} className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="radio"
                            name="errorReportType"
                            value={val}
                            checked={errorReportType === val}
                            onChange={() => setErrorReportType(val)}
                            className="text-blue-600"
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Filter mode */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Filter By</label>
                    <div className="flex gap-4">
                      {[['date', 'Client & Date Range'], ['job', 'Job Code']].map(([val, label]) => (
                        <label key={val} className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="radio"
                            name="errorReportMode"
                            value={val}
                            checked={errorReportMode === val}
                            onChange={() => setErrorReportMode(val)}
                            className="text-blue-600"
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {errorReportMode === 'job' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Job Code(s)</label>
                      <textarea
                        value={errorReportJobCodes}
                        onChange={e => setErrorReportJobCodes(e.target.value)}
                        rows={3}
                        placeholder="One per line or comma-separated, e.g. 260313327, 332211"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                      />
                      <p className="text-xs text-gray-600 mt-1">Client and date filters are ignored in this mode.</p>
                    </div>
                  )}

                  {/* Client filter */}
                  {errorReportMode === 'date' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Clients</label>
                    <div className="relative min-w-[250px]" style={{ zIndex: 17 }}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white flex items-center justify-between"
                        onClick={() => setShowErrorClientDropdown(v => !v)}
                      >
                        <span className="truncate">
                          {errorReportClients.length > 0
                            ? errorReportClients.length === clients.length
                              ? 'All Clients'
                              : errorReportClients.join(', ')
                            : 'All Clients'}
                        </span>
                        <svg className="w-4 h-4 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {showErrorClientDropdown && (
                        <div className="absolute left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto z-30">
                          <div className="p-2 border-b border-gray-100">
                            <input
                              type="text"
                              placeholder="Search..."
                              value={errorClientDropdownSearch}
                              onChange={e => setErrorClientDropdownSearch(e.target.value)}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                              autoFocus
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            <label className="flex items-center px-2 py-1 cursor-pointer hover:bg-gray-50 rounded">
                              <input
                                type="checkbox"
                                checked={errorReportClients.length === clients.length && clients.length > 0}
                                onChange={e => setErrorReportClients(e.target.checked ? clients.map(c => c.name) : [])}
                                className="mr-2"
                              />
                              <span className="text-sm">Select All</span>
                            </label>
                            {clients
                              .filter(c => !errorClientDropdownSearch || c.name.toLowerCase().includes(errorClientDropdownSearch.toLowerCase()))
                              .map(c => (
                                <label key={c.name} className="flex items-center px-2 py-1 cursor-pointer hover:bg-gray-50 rounded">
                                  <input
                                    type="checkbox"
                                    checked={errorReportClients.includes(c.name)}
                                    onChange={e => {
                                      if (e.target.checked) {
                                        setErrorReportClients(prev => Array.from(new Set([...prev, c.name])));
                                      } else {
                                        setErrorReportClients(prev => prev.filter(n => n !== c.name));
                                      }
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">{c.name}</span>
                                </label>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  )}

                  {/* Date range */}
                  {errorReportMode === 'date' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">From <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        value={errorReportFrom}
                        onChange={e => setErrorReportFrom(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">To <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        value={errorReportTo}
                        onChange={e => setErrorReportTo(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        required
                      />
                    </div>
                  </div>
                  )}

                  {/* Download button */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleExportErrorReport}
                      disabled={errorReportLoading}
                      className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium transition-colors"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {errorReportLoading ? 'Downloading…' : 'Download Report'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
            )}
          </div>
          </>
          

          {/* Modal for summary table */}
          {(isAdmin || isPM) && showUserSummaryModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-xl font-semibold text-gray-800">User Time Tracking</h3>
                    <button onClick={() => setShowUserSummaryModal(false)} className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100" title="Close">
                      <span style={{fontSize: '1.5em', lineHeight: 1}}>&times;</span>
                    </button>
                  </div>
                  
                  {userSummaryLoading ? (
                    <div className="p-8 text-center text-gray-500">Loading summary...</div>
                  ) : aggregatedUserSummary.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">No summary data available</div>
                  ) : (
                    <>
                      {/* Summary Cards */}
                      <div className="grid grid-cols-3 gap-4 p-6 border-b border-gray-200">
                        <div className="text-center border-r border-gray-200">
                          <p className="text-sm text-gray-500 mb-1">Total Users</p>
                          <p className="text-4xl font-light text-gray-800">{aggregatedUserSummary.length}</p>
                        </div>
                        <div className="text-center border-r border-gray-200">
                          <p className="text-sm text-blue-600 mb-1">Tasks Completed</p>
                          <p className="text-4xl font-light text-gray-800">{aggregatedUserSummary.reduce((sum, u) => sum + (u.tasks_done || 0), 0)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-green-600 mb-1">Projects Completed</p>
                          <p className="text-4xl font-light text-gray-800">{aggregatedUserSummary.reduce((sum, u) => sum + (u.projects_done || 0), 0)}</p>
                        </div>
                      </div>

                      {/* Time Tracked Section */}
                      <div className="p-6 flex-1 overflow-y-auto">
                        <h4 className="text-lg font-semibold text-gray-800 mb-3">Time Tracked</h4>
                        
                        {/* Legend */}
                        <div className="flex flex-wrap gap-4 mb-4 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-sm bg-green-500"></span>
                            <span className="text-gray-600">Job Time</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-sm bg-gray-400"></span>
                            <span className="text-gray-600">Break Time</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-sm bg-red-400"></span>
                            <span className="text-gray-600">Idle Time</span>
                          </div>
                        </div>

                        {/* Total Summary Row */}
                        <div className="flex items-center py-3 border-b border-gray-200 mb-2">
                          <div className="w-40 font-medium text-gray-800">
                            {aggregatedUserSummary.length} Users
                          </div>
                          <div className="w-24 font-medium text-gray-800">
                            {(() => {
                              const totalHours = aggregatedUserSummary.reduce((sum, u) => sum + (u.total_shift_hours || 0), 0);
                              const h = Math.floor(totalHours);
                              const m = Math.round((totalHours - h) * 60);
                              return `${h}h ${m}m`;
                            })()}
                          </div>
                        </div>

                        {/* User Rows */}
                        <div className="space-y-1">
                          {aggregatedUserSummary
                            .map((row, idx) => {
                              const maxHours = Math.max(...aggregatedUserSummary.map(u => u.total_shift_hours || 0), 1);
                              const totalPercent = ((row.total_shift_hours || 0) / maxHours) * 100;
                              const jobPercent = row.total_shift_hours > 0 ? ((row.job_hours || 0) / row.total_shift_hours) * totalPercent : 0;
                              const breakPercent = row.total_shift_hours > 0 ? ((row.break_hours || 0) / row.total_shift_hours) * totalPercent : 0;
                              const idlePercent = row.total_shift_hours > 0 ? ((row.idle_hours || 0) / row.total_shift_hours) * totalPercent : 0;
                              const h = Math.floor(row.total_shift_hours || 0);
                              const m = Math.round(((row.total_shift_hours || 0) - h) * 60);
                              
                              return (
                                <div key={row.user_name + idx} className="flex items-center py-2 hover:bg-gray-50 rounded">
                                  <div className="w-40 text-sm text-gray-700 truncate" title={row.user_name}>
                                    <div className="font-medium">{row.user_name}</div>
                                    {row.department && <div className="text-xs text-gray-400">{row.department}</div>}
                                  </div>
                                  <div className="w-24 text-sm text-gray-600">
                                    {h}h {m}m
                                  </div>
                                  <div className="flex-1 flex h-5 bg-gray-100 rounded overflow-hidden">
                                    {jobPercent > 0 && (
                                      <div 
                                        className="bg-green-500 h-full" 
                                        style={{ width: `${jobPercent}%` }}
                                        title={`Job: ${formatHoursHms(row.job_hours)}`}
                                      ></div>
                                    )}
                                    {breakPercent > 0 && (
                                      <div 
                                        className="bg-gray-400 h-full" 
                                        style={{ width: `${breakPercent}%` }}
                                        title={`Break: ${formatHoursHms(row.break_hours)}`}
                                      ></div>
                                    )}
                                    {idlePercent > 0 && (
                                      <div 
                                        className="bg-red-400 h-full" 
                                        style={{ width: `${idlePercent}%` }}
                                        title={`Idle: ${formatHoursHms(row.idle_hours)}`}
                                      ></div>
                                    )}
                                  </div>
                                  <div className="w-48 text-right text-xs text-gray-500 ml-2 flex flex-col">
                                    <span>{row.tasks_done || 0} tasks, {row.projects_done || 0} projects</span>
                                    <span className="text-gray-400">
                                      Lunch {formatHoursHms(row.break_lunch || 0)} | Snacks {formatHoursHms(row.break_snacks || 0)} | Dinner {formatHoursHms(row.break_dinner || 0)}
                                    </span>
                                    <span className="text-gray-400">
                                      Idle {formatHoursHms(row.idle_hours || 0)} | No Jobs {formatHoursHms(row.idle_no_jobs || 0)} | Non Track {formatHoursHms(row.non_tracking_hours || 0)}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                    <button onClick={() => setShowUserSummaryModal(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors font-medium">Close</button>
                  </div>
                </div>
              </div>
            )}

          {/* Modal for Team Utilization results */}
          {(isAdmin || isPM) && showTeamUtilModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-gray-800">Team Utilization & Efficiency</h3>
                  <button onClick={() => setShowTeamUtilModal(false)} className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100" title="Close">
                    <span style={{fontSize: '1.5em', lineHeight: 1}}>&times;</span>
                  </button>
                </div>

                {teamUtilLoading ? (
                  <div className="p-8 text-center text-gray-500">Loading team data...</div>
                ) : teamData.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">No team data available</div>
                ) : (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-3 gap-4 p-6 border-b border-gray-200">
                      <div className="text-center border-r border-gray-200">
                        <p className="text-sm text-gray-500 mb-1">Total Teams</p>
                        <p className="text-4xl font-light text-gray-800">{teamData.length}</p>
                      </div>
                      <div className="text-center border-r border-gray-200">
                        <p className="text-sm text-blue-600 mb-1">Total Members</p>
                        <p className="text-4xl font-light text-gray-800">{teamData.reduce((sum, t) => sum + (t.member_count || 0), 0)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-green-600 mb-1">Tasks Completed</p>
                        <p className="text-4xl font-light text-gray-800">{teamData.reduce((sum, t) => sum + (t.tasks_completed || 0), 0)}</p>
                      </div>
                    </div>

                    {/* Team Data Table */}
                    <div className="flex-1 overflow-y-auto p-6">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider">
                              <th className="px-4 py-3 font-medium">Team Name</th>
                              <th className="px-4 py-3 font-medium">Type</th>
                              <th className="px-4 py-3 font-medium">Members</th>
                              <th className="px-4 py-3 font-medium">Total Shift</th>
                              <th className="px-4 py-3 font-medium">Job Hours</th>
                              <th className="px-4 py-3 font-medium">Break Hours</th>
                              <th className="px-4 py-3 font-medium">Lunch</th>
                              <th className="px-4 py-3 font-medium">Snacks</th>
                              <th className="px-4 py-3 font-medium">Dinner</th>
                              <th className="px-4 py-3 font-medium">Idle Hours</th>
                              <th className="px-4 py-3 font-medium">Tasks</th>
                              <th className="px-4 py-3 font-medium">Utilization</th>
                              <th className="px-4 py-3 font-medium">Efficiency</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {teamData.map((team) => (
                              <tr key={team.team_id} className="hover:bg-gray-50">
                                <td className="px-4 py-4 text-sm font-medium text-gray-900">{team.team_name}</td>
                                <td className="px-4 py-4 text-sm text-gray-500 capitalize">{team.team_type}</td>
                                <td className="px-4 py-4 text-sm text-gray-500">{team.member_count}</td>
                                <td className="px-4 py-4 text-sm text-gray-500">{formatHoursHms(team.total_shift_hours)}</td>
                                <td className="px-4 py-4 text-sm text-green-600">{formatHoursHms(team.job_hours)}</td>
                                <td className="px-4 py-4 text-sm text-gray-500" title={`Lunch: ${formatHoursHms(team.break_lunch)}, Snacks: ${formatHoursHms(team.break_snacks)}, Dinner: ${formatHoursHms(team.break_dinner)}`}>
                                  {formatHoursHms(team.break_hours)}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500">
                                  {formatHoursHms(team.break_lunch)}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500">
                                  {formatHoursHms(team.break_snacks)}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500">
                                  {formatHoursHms(team.break_dinner)}
                                </td>
                                <td className="px-4 py-4 text-sm text-red-500" title={`Idle (Meeting/Call/Training): ${formatHoursHms(team.idle_hours)} | No Jobs: ${formatHoursHms(team.idle_no_jobs)}`}>
                                  {formatHoursHms(team.idle_hours)}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500">{team.tasks_completed}</td>
                                <td className="px-4 py-4 text-sm">
                                  <div className="flex items-center">
                                    <div className="w-12 bg-gray-200 rounded-full h-2 mr-2">
                                      <div 
                                        className="bg-blue-600 h-2 rounded-full" 
                                        style={{ width: `${team.utilization_percentage}%` }}
                                      ></div>
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">{team.utilization_percentage}%</span>
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-sm">
                                  <div className="flex items-center">
                                    <div className="w-12 bg-gray-200 rounded-full h-2 mr-2">
                                      <div 
                                        className="bg-green-600 h-2 rounded-full" 
                                        style={{ width: `${team.efficiency_percentage}%` }}
                                      ></div>
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">{team.efficiency_percentage}%</span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}

                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                  <button onClick={() => setShowTeamUtilModal(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors font-medium">Close</button>
                </div>
              </div>
            </div>
          )}

          {/* Modal for Completed Projects results */}
          {(isAdmin || isPM) && showCompletedProjModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-gray-800">Completed Projects Report</h3>
                  <button onClick={() => setShowCompletedProjModal(false)} className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100" title="Close">
                    <span style={{fontSize: '1.5em', lineHeight: 1}}>&times;</span>
                  </button>
                </div>

                {completedProjLoading ? (
                  <div className="p-8 text-center text-gray-500">Loading completed projects...</div>
                ) : completedProjData.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">No completed projects found</div>
                ) : (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-3 gap-4 p-6 border-b border-gray-200">
                      <div className="text-center border-r border-gray-200">
                        <p className="text-sm text-gray-500 mb-1">Total Projects</p>
                        <p className="text-4xl font-light text-gray-800">{completedProjData.length}</p>
                      </div>
                      <div className="text-center border-r border-gray-200">
                        <p className="text-sm text-blue-600 mb-1">Unique Clients</p>
                        <p className="text-4xl font-light text-gray-800">{new Set(completedProjData.map(p => p.client)).size}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-green-600 mb-1">Date Range</p>
                        <p className="text-lg font-light text-gray-800">{completedProjFrom} to {completedProjTo}</p>
                      </div>
                    </div>

                    {/* Projects Table */}
                    <div className="flex-1 overflow-y-auto p-6">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider">
                              <th className="px-4 py-3 font-medium">Job Number</th>
                              <th className="px-4 py-3 font-medium">Project Title</th>
                              <th className="px-4 py-3 font-medium">Client</th>
                              <th className="px-4 py-3 font-medium">Complexity</th>
                              <th className="px-4 py-3 font-medium">Completed Date</th>
                              <th className="px-4 py-3 font-medium">Created Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {completedProjData.map((project) => (
                              <tr key={project.id || project._id} className="hover:bg-gray-50">
                                <td className="px-4 py-4 text-sm font-medium text-gray-900">{project.title}</td>
                                <td className="px-4 py-4 text-sm text-gray-500">{project.client}</td>
                                <td className="px-4 py-4 text-sm text-gray-500">{project.job_code || '-'}</td>
                                <td className="px-4 py-4 text-sm text-gray-500 capitalize">{project.complexity || '-'}</td>
                                <td className="px-4 py-4 text-sm text-gray-500">
                                  {project.archived_at ? new Date(project.archived_at).toLocaleDateString() : '-'}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500">
                                  {project.created_at ? new Date(project.created_at).toLocaleDateString() : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}

                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                  <button onClick={() => setShowCompletedProjModal(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors font-medium">Close</button>
                </div>
              </div>
            </div>
          )}

          {/* Modal for Active Projects results */}
          {(isAdmin || isPM) && showActiveProjModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-gray-800">Active Projects Report</h3>
                  <button onClick={() => setShowActiveProjModal(false)} className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100" title="Close">
                    <span style={{fontSize: '1.5em', lineHeight: 1}}>&times;</span>
                  </button>
                </div>

                {activeProjLoading ? (
                  <div className="p-8 text-center text-gray-500">Loading active projects...</div>
                ) : activeProjData.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">No active projects found</div>
                ) : (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-4 gap-4 p-6 border-b border-gray-200">
                      <div className="text-center border-r border-gray-200">
                        <p className="text-sm text-gray-500 mb-1">Total Projects</p>
                        <p className="text-4xl font-light text-gray-800">{activeProjData.length}</p>
                      </div>
                      <div className="text-center border-r border-gray-200">
                        <p className="text-sm text-blue-600 mb-1">Unique Clients</p>
                        <p className="text-4xl font-light text-gray-800">{new Set(activeProjData.map(p => p.client)).size}</p>
                      </div>
                      <div className="text-center border-r border-gray-200">
                        <p className="text-sm text-orange-600 mb-1">Total Pending Tasks</p>
                        <p className="text-4xl font-light text-gray-800">{activeProjData.reduce((s, p) => s + (p.pending_tasks || 0), 0)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-green-600 mb-1">Total Completed Tasks</p>
                        <p className="text-4xl font-light text-gray-800">{activeProjData.reduce((s, p) => s + (p.completed_tasks || 0), 0)}</p>
                      </div>
                    </div>

                    {/* Projects Table */}
                    <div className="flex-1 overflow-y-auto p-6">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-sm">
                          <thead>
                            <tr className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                              <th className="px-3 py-3 font-medium">Job #</th>
                              <th className="px-3 py-3 font-medium">Project Title</th>
                              <th className="px-3 py-3 font-medium">Client</th>
                              <th className="px-3 py-3 font-medium">Complexity</th>
                              <th className="px-3 py-3 font-medium">Current Stage</th>
                              <th className="px-3 py-3 font-medium">Stage Status</th>
                              <th className="px-3 py-3 font-medium">Progress</th>
                              <th className="px-3 py-3 font-medium">Tasks</th>
                              <th className="px-3 py-3 font-medium">Created Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {activeProjData.map((project) => {
                              const stageStatusColor =
                                project.current_stage_status === 'in_progress' ? 'text-blue-600' :
                                project.current_stage_status === 'paused' ? 'text-yellow-600' :
                                project.current_stage_status === 'not_started' ? 'text-gray-500' :
                                project.current_stage_status === 'on_hold' ? 'text-orange-600' :
                                project.current_stage_status === 'query' ? 'text-purple-600' :
                                'text-gray-500';
                              return (
                                <tr key={project.id || project._id} className="hover:bg-gray-50">
                                  <td className="px-3 py-3 font-medium text-gray-900 whitespace-nowrap">{project.job_code || '-'}</td>
                                  <td className="px-3 py-3 text-gray-800 max-w-xs truncate" title={project.title}>{project.title}</td>
                                  <td className="px-3 py-3 text-gray-500 whitespace-nowrap">{project.client || '-'}</td>
                                  <td className="px-3 py-3 text-gray-500 capitalize whitespace-nowrap">{project.complexity || '-'}</td>
                                  <td className="px-3 py-3 text-gray-800 font-medium whitespace-nowrap">
                                    <div>{project.current_stage || '-'}</div>
                                    {project.current_stage_team && (
                                      <div className="text-xs text-gray-400 capitalize">{project.current_stage_team}</div>
                                    )}
                                  </td>
                                  <td className={`px-3 py-3 capitalize whitespace-nowrap ${stageStatusColor}`}>
                                    {(project.current_stage_status || '').replace(/_/g, ' ') || '-'}
                                  </td>
                                  <td className="px-3 py-3 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <div className="w-16 bg-gray-200 rounded-full h-2">
                                        <div
                                          className="bg-green-500 h-2 rounded-full"
                                          style={{ width: `${project.completion_percentage || 0}%` }}
                                        />
                                      </div>
                                      <span className="text-xs text-gray-600">{project.completion_percentage || 0}%</span>
                                    </div>
                                  </td>
                                  <td className="px-3 py-3 whitespace-nowrap">
                                    <span className="text-green-600">{project.completed_tasks || 0}</span>
                                    <span className="text-gray-400 mx-1">/</span>
                                    <span className="text-gray-700">{project.total_tasks || 0}</span>
                                    {(project.pending_tasks || 0) > 0 && (
                                      <span className="ml-1 text-xs text-orange-500">({project.pending_tasks} left)</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-3 text-gray-500 whitespace-nowrap">
                                    {project.created_at ? new Date(project.created_at).toLocaleDateString() : '-'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}

                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                  <button onClick={() => setShowActiveProjModal(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors font-medium">Close</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detailed Report Table */}
      
    </div>
  );
};

export default Reports;